import React, { memo, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { SectionList } from '@Sage/ssm-web5';
import { getLiteral } from 'utils/getLiteral';
import ErrorNoRows from './ErrorNoRows';
import ProductRow from './ProductRow';

const AddProductsTable = memo(
    ({
        id,
        isLoading,
        hasError,
        currencySymbol,
        idEnvironment,
        idCurrency,
        quantityMap,
        onClickFolder,
        useLazyLoad = false,
        rows,
        initTable,
        getTotal,
        pageSize = 50,
        onRef,
    }) => {
        const locale = useSelector((state) => state?.config?.userData?.locale);
        const columns = useMemo(
            () => [
                {
                    colId: 'productItem',
                    headerName: getLiteral('title_products'),
                    headerComponent: 'headerTextCell',
                    headerComponentParams: {
                        showTotal: true,
                    },
                    cellRenderer: ProductRow,
                    cellRendererParams: {
                        currencySymbol,
                        idEnvironment,
                        idCurrency,
                        quantityMap,
                        onClickFolder,
                    },
                    resizable: false,
                },
            ],
            [currencySymbol, idCurrency, idEnvironment, onClickFolder, quantityMap],
        );

        const getRowNodeId = useCallback(
            (item) => (item.isProductFolder ? `folder-${item.id}` : `product-${item.id}`),
            [],
        );

        const emptyViewProps = useCallback(
            () => ({
                title: getLiteral('error_notresultfound'),
                subtitle: getLiteral('helptext_search_filter_text'),
                icon: null,
            }),
            [],
        );

        const errorViewProps = useCallback(
            () => ({
                title: getLiteral('error_an_error_occurred'),
                subtitle: getLiteral('error_reload_table'),
            }),
            [],
        );

        // For non-lazy mode, show ErrorNoRows if no data
        if (!useLazyLoad && !isLoading && hasError) {
            return <ErrorNoRows />;
        }

        // Props that only apply to lazy load mode
        const lazyLoadProps = useLazyLoad
            ? {
                  initTable,
                  getTotal,
                  pageSize,
                  useLoaderPlaceholder: true,
                  initOnReady: true,
              }
            : {};

        return (
            <SectionList
                id={id}
                className="fm-add-products-modal__table"
                columns={columns}
                rows={useLazyLoad ? null : rows || []}
                rowHeight={64}
                isLoading={isLoading}
                hasError={hasError}
                getRowNodeId={getRowNodeId}
                rowBuffer={10}
                useLazyLoad={useLazyLoad}
                emptyViewProps={emptyViewProps}
                errorViewProps={errorViewProps}
                onRef={onRef}
                domLayout="autoHeight"
                locale={locale}
                {...lazyLoadProps}
            />
        );
    },
);

export default AddProductsTable;
